import { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import PremiumDatePicker from "../components/PremiumDatePicker";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";
import { ChartBar, Download, Calendar, CurrencyDollar, Receipt, Package, Warning, ArrowUp, ArrowDown, Minus, Wallet } from "@phosphor-icons/react";
import TyroReconciliationCard from "../components/TyroReconciliationCard";

/**
 * Sales reports — per-store comparison with 10 preset date ranges plus a
 * custom YYYY-MM-DD range. Admins get a comparison table across every store
 * the business owns; non-admin staff see a single card pinned to whichever
 * store they currently have active in the sidebar.
 */

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_7", label: "Last 7 days" },
  { key: "this_week", label: "This week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_year", label: "This year" },
  { key: "last_year", label: "Last year" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString();

export default function Reports() {
  const { user } = useAuth();
  const { currentStore } = useStore();
  const isAdmin = user?.role === "admin";

  const [rangeKey, setRangeKey] = useState("today");
  const [custom, setCustom] = useState(() => {
    const t = new Date();
    const iso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    return { start: iso, end: iso };
  });
  // Admin-only: "all" aggregates every store; else a specific id.
  const [storeFilter, setStoreFilter] = useState("all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range: rangeKey });
      if (rangeKey === "custom") {
        if (!custom.start || !custom.end) {
          toast.error("Pick both a start and end date");
          setLoading(false);
          return;
        }
        params.set("start", custom.start);
        params.set("end", custom.end);
      }
      if (isAdmin) params.set("store_id", storeFilter);
      const { data } = await api.get(`/reports/sales?${params.toString()}`);
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, storeFilter, custom.start, custom.end, currentStore?.id]);

  // Export the current report as CSV so accountants can drop it into Excel.
  const exportCsv = () => {
    if (!report?.stores?.length) return toast.error("Nothing to export");
    const header = ["Store", "Sales", "Revenue", "Avg Order", "Items Sold",
      "Paid Revenue", "Deposit Revenue", "Outstanding", "Tax", "Discount"];
    const rows = report.stores.map((s) => [
      s.store_name, s.sale_count, s.revenue, s.avg_order, s.item_count,
      s.paid_revenue, s.deposit_revenue, s.outstanding_balance, s.tax, s.discount,
    ]);
    const t = report.totals;
    rows.push(["TOTAL", t.sale_count, t.revenue, t.avg_order, t.item_count,
      t.paid_revenue, t.deposit_revenue, t.outstanding_balance, t.tax, t.discount]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report_${rangeKey}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = report?.totals || { sale_count: 0, revenue: 0, avg_order: 0, outstanding_balance: 0, item_count: 0 };
  const rangeLabel = report?.label || "—";
  const prev = report?.previous;  // {label, totals:{revenue, sale_count, avg_order, item_count}} | null

  return (
    <Layout>
      <PageHeader
        title="Reports"
        subtitle={isAdmin ? "Compare sales across every store" : `Sales for ${currentStore?.name || "your store"}`}
        icon={ChartBar}
      />

      <div className="px-6 lg:px-8 pt-4 pb-12 space-y-6" data-testid="reports-page">
        {/* ——— Filter bar ——— */}
        <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5">
          <div className="flex flex-wrap items-center gap-2" data-testid="range-presets">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => setRangeKey(p.key)}
                data-testid={`range-${p.key}`}
                className={`h-9 px-4 rounded-full text-[12px] font-semibold tracking-wide transition-colors border ${
                  rangeKey === p.key
                    ? "bg-[#121216] text-white border-[#121216]"
                    : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#121216] hover:text-[#121216]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom date pickers */}
          {rangeKey === "custom" && (
            <div className="mt-4 flex flex-wrap items-end gap-3" data-testid="custom-range">
              <PremiumDatePicker
                label="From"
                value={custom.start}
                onChange={(v) => setCustom((c) => ({ ...c, start: v }))}
                testid="custom-start"
                maxDate={custom.end}
              />
              <PremiumDatePicker
                label="To"
                value={custom.end}
                onChange={(v) => setCustom((c) => ({ ...c, end: v }))}
                testid="custom-end"
                minDate={custom.start}
              />
            </div>
          )}

          {/* Admin-only store filter */}
          {isAdmin && (
            <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="store-filter">
              <span className="text-[11px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold">Stores</span>
              <select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                className="h-9 px-3 text-[12px] rounded-lg border border-[#E5E7EB] bg-white hover:border-[#121216] focus:outline-none focus:border-[#1E5FEB]"
                data-testid="store-select"
              >
                <option value="all">All stores (comparison)</option>
                {(report?.stores || []).map((s) => (
                  <option key={s.store_id} value={s.store_id}>{s.store_name}</option>
                ))}
              </select>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[11px] text-[#9CA3AF]">
                  <Calendar size={12} weight="bold" className="inline mr-1" />
                  {rangeLabel}
                  {report?.start && ` · ${report.start.slice(0, 10)} → ${report.end.slice(0, 10)}`}
                </span>
                <button
                  onClick={exportCsv}
                  data-testid="export-csv"
                  className="h-9 px-3 rounded-full bg-[#121216] hover:bg-[#1E5FEB] text-white text-[11px] font-semibold flex items-center gap-1.5 transition-colors"
                >
                  <Download size={12} weight="bold" /> EXPORT CSV
                </button>
              </div>
            </div>
          )}
          {!isAdmin && (
            <div className="mt-4 text-[11px] text-[#9CA3AF]">
              <Calendar size={12} weight="bold" className="inline mr-1" />
              {rangeLabel}
              {report?.start && ` · ${report.start.slice(0, 10)} → ${report.end.slice(0, 10)}`}
            </div>
          )}
        </section>

        {/* ——— KPI tiles ——— */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-tiles">
          <KPI
            title="Revenue"
            value={money(totals.revenue_total ?? totals.revenue)}
            icon={CurrencyDollar}
            tone="brand"
            hint={
              loading ? "Loading…"
              : totals.history_revenue > 0.005
                ? `${num(totals.sale_count)} live sales · incl. ${money(totals.history_revenue)} history`
                : `${num(totals.sale_count)} sales`
            }
            trend={<TrendChip current={totals.revenue} previous={prev?.totals?.revenue} prevLabel={prev?.label} kind="money" />}
          />
          <KPI title="Avg order" value={money(totals.avg_order)} icon={Receipt} tone="ok"
               trend={<TrendChip current={totals.avg_order} previous={prev?.totals?.avg_order} prevLabel={prev?.label} kind="money" />} />
          <KPI title="Items sold" value={num(totals.item_count)} icon={Package} tone="mute"
               trend={<TrendChip current={totals.item_count} previous={prev?.totals?.item_count} prevLabel={prev?.label} kind="count" />} />
          <KPI title="Outstanding" value={money(totals.outstanding_balance)}
               icon={Warning} tone={totals.outstanding_balance > 0.005 ? "warn" : "mute"} />
        </section>

        {/* ——— Cash-out summary ——— */}
        {/* ——— Payments Received (cash-basis view) ———
            Sums money that actually came in the door during this window,
            regardless of when the original sale was rung up. Deposit on
            Day 1 + balance on Day 2 → each amount lands on the day it was
            paid. Critical for daily bank reconciliation + drawer counts. */}
        {((totals.payments_received_count || 0) > 0
          || (totals.refunds_paid_out_count || 0) > 0) && (
          <section
            className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden"
            data-testid="reports-payments-received"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <div className="min-w-0">
                <div className="overline">Payments received <span className="text-[9px] text-[#9CA3AF] normal-case tracking-normal">cash-basis</span></div>
                <div className="font-display font-semibold text-[18px] tracking-tight flex flex-wrap items-baseline gap-x-3">
                  <span className="text-[#0F766E] tabular-nums">+ {money(totals.payments_received_total || 0)}</span>
                  {(totals.refunds_paid_out_count || 0) > 0 && (
                    <span className="text-[#B91C1C] tabular-nums text-[15px]">− {money(totals.refunds_paid_out_total || 0)}</span>
                  )}
                  <span className="text-[13px] text-[#9CA3AF] font-normal">
                    · net {money(totals.net_cash_movement || 0)}
                  </span>
                </div>
                <div className="text-[11px] text-[#9CA3AF] mt-1">
                  Money in the door this period, including deposits & later balance payments — not the same as Revenue.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-y divide-[#F3F4F6]">
              {Object.entries(totals.payments_received_by_method || {})
                .sort(([, a], [, b]) => b - a)
                .map(([m, amt]) => (
                  <div key={m} className="px-4 py-3" data-testid={`payments-received-method-${m}`}>
                    <div className="text-[10.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold">
                      {m === "card_manual" ? "Card" : m === "tyro" ? "EFTPOS · Tyro" : m.toUpperCase()}
                    </div>
                    <div className="text-[17px] font-display font-semibold tabular-nums mt-0.5 text-[#0F766E]">+ {money(amt)}</div>
                  </div>
                ))}
              {Object.entries(totals.refunds_paid_out_by_method || {})
                .sort(([, a], [, b]) => b - a)
                .map(([m, amt]) => (
                  <div key={`r-${m}`} className="px-4 py-3" data-testid={`refunds-paid-method-${m}`}>
                    <div className="text-[10.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold">
                      Refund · {m === "card_manual" ? "Card" : m === "tyro" ? "EFTPOS" : m === "store_credit" ? "Store credit" : m.toUpperCase()}
                    </div>
                    <div className="text-[17px] font-display font-semibold tabular-nums mt-0.5 text-[#B91C1C]">− {money(amt)}</div>
                  </div>
                ))}
            </div>
          </section>
        )}

        {(Number(totals.store_credit_issued || 0) > 0 || Number(totals.store_credit_used || 0) > 0 || Number(totals.store_credit_outstanding || 0) > 0) && (
          <section className="rounded-2xl border border-emerald-200 bg-white overflow-hidden" data-testid="reports-store-credit">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-emerald-100 bg-emerald-50/60">
              <div className="w-9 h-9 rounded-full bg-emerald-700 flex items-center justify-center">
                <Wallet size={16} weight="fill" className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="overline text-emerald-800">Store credit</div>
                <div className="text-[11px] text-[#64748B] mt-0.5">No money moves — credit issued or spent in store. Not included in payments or net cash.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#F3F4F6]">
              <div className="px-5 py-4" data-testid="reports-store-credit-issued">
                <div className="text-[10.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Issued</div>
                <div className="text-[20px] font-display font-semibold tabular-nums text-emerald-700">+ {money(totals.store_credit_issued || 0)}</div>
                <div className="text-[11px] text-[#64748B] mt-0.5">
                  Refunds {money(totals.store_credit_issued_refunds || 0)} · Trade-ins {money(totals.store_credit_issued_trade_ins || 0)}
                </div>
              </div>
              <div className="px-5 py-4" data-testid="reports-store-credit-used">
                <div className="text-[10.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Used at checkout</div>
                <div className="text-[20px] font-display font-semibold tabular-nums text-[#B45309]">− {money(totals.store_credit_used || 0)}</div>
                <div className="text-[11px] text-[#64748B] mt-0.5">Taken off sale totals</div>
              </div>
              <div className="px-5 py-4" data-testid="reports-store-credit-outstanding">
                <div className="text-[10.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Outstanding now</div>
                <div className="text-[20px] font-display font-semibold tabular-nums text-[#121216]">{money(totals.store_credit_outstanding || 0)}</div>
                <div className="text-[11px] text-[#64748B] mt-0.5">Held by {totals.store_credit_customers || 0} customer{totals.store_credit_customers === 1 ? "" : "s"} · all stores</div>
              </div>
            </div>
          </section>
        )}

        {/* Till cash-outs (trade-in payouts, bank deposits, petty cash, etc.) are
            NOT sales — they're money LEAVING the drawer — so they can't be
            rolled into the Revenue KPI. We surface them in their own card so
            the shop owner can reconcile physical cash at end-of-day:
              (opening float) + (cash sales) − (refunds) − (cash_out_total) = expected drawer. */}
        {(totals.cash_out_count > 0 || (totals.cash_out_by_category && Object.keys(totals.cash_out_by_category).length > 0)) && (
          <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="reports-cash-out-summary">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <div>
                <div className="overline">Cash out from till</div>
                <div className="font-display font-semibold text-[18px] tracking-tight">
                  {money(totals.cash_out_total || 0)} <span className="text-[13px] text-[#9CA3AF] font-normal">· {num(totals.cash_out_count || 0)} entr{(totals.cash_out_count || 0) === 1 ? "y" : "ies"}</span>
                </div>
              </div>
              <a href="/till" className="text-[11px] uppercase tracking-wider font-semibold text-[#1E5FEB] hover:underline">Open till →</a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-y divide-[#F3F4F6]">
              {Object.entries(totals.cash_out_by_category || {})
                .sort(([, a], [, b]) => b - a)
                .map(([cat, amt]) => (
                  <div key={cat} className="px-4 py-3" data-testid={`cash-out-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="text-[10.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold">{cat}</div>
                    <div className="text-[17px] font-display font-semibold tabular-nums mt-0.5 text-[#B91C1C]">{money(amt)}</div>
                  </div>
                ))}
              {!Object.keys(totals.cash_out_by_category || {}).length && (
                <div className="p-8 text-[12px] text-[#9CA3AF] col-span-4">No cash-outs in this range.</div>
              )}
            </div>
          </section>
        )}

        {/* ——— Per-store comparison ——— */}
        <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="store-compare">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
            <div>
              <div className="overline">{isAdmin ? "Store comparison" : "Your store"}</div>
              <div className="font-display font-semibold text-[18px] tracking-tight">
                {rangeLabel} · {report?.stores?.length || 0} store{(report?.stores?.length || 0) === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          {loading ? (
            <div className="p-8 text-[12px] text-[#9CA3AF]">Loading…</div>
          ) : !report?.stores?.length ? (
            <div className="p-8 text-[12px] text-[#9CA3AF]">No data for this range.</div>
          ) : (
            <StoreTable stores={report.stores} totals={report.totals} showTotal={isAdmin && report.stores.length > 1} />
          )}
        </section>

        {/* ——— Payment method breakdown ——— */}
        {report?.stores?.length ? (
          <section className="grid md:grid-cols-2 gap-4" data-testid="payment-breakdown">
            {report.stores.map((s) => (
              <PaymentCard key={s.store_id} store={s} />
            ))}
          </section>
        ) : null}
      </div>
    </Layout>
  );
}

function KPI({ title, value, icon: Icon, tone = "mute", hint, trend }) {
  const tones = {
    brand: "bg-[#DBEAFE] text-[#1E40AF]",
    ok: "bg-[#DCFCE7] text-[#166534]",
    warn: "bg-[#FEE2E2] text-[#B91C1C]",
    mute: "bg-[#F3F4F6] text-[#4B5563]",
  }[tone];
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5">
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold">{title}</div>
        {Icon && <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tones}`}>
          <Icon size={15} weight="duotone" />
        </div>}
      </div>
      <div className="mt-2 font-display text-[26px] font-bold tracking-tight text-[#121216]">{value}</div>
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        {hint && <div className="text-[11px] text-[#9CA3AF]">{hint}</div>}
        {trend}
      </div>
    </div>
  );
}

/**
 * Growth chip shown next to each KPI. Computes the % delta between the current
 * value and the previous-period value returned by the backend. Hover reveals
 * the absolute delta plus the period label (e.g. "Last month: $34,221").
 */
function TrendChip({ current, previous, prevLabel, kind = "money" }) {
  if (previous === undefined || previous === null || !prevLabel) return null;
  const cur = Number(current || 0);
  const prv = Number(previous || 0);
  const fmt = (v) =>
    kind === "money"
      ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : v.toLocaleString();

  let pct = 0, dir = "flat";
  if (prv === 0) {
    pct = cur > 0 ? 100 : 0;
    dir = cur > 0 ? "up" : "flat";
  } else {
    pct = ((cur - prv) / prv) * 100;
    dir = pct > 0.05 ? "up" : pct < -0.05 ? "down" : "flat";
  }
  const tone = {
    up: "bg-[#DCFCE7] text-[#166534]",
    down: "bg-[#FEE2E2] text-[#B91C1C]",
    flat: "bg-[#F3F4F6] text-[#4B5563]",
  }[dir];
  const Icon = dir === "up" ? ArrowUp : dir === "down" ? ArrowDown : Minus;
  const absPct = Math.abs(pct);
  // Cap the displayed % — a fresh-reset store showing "▲ 842,000%" is noise.
  const label = absPct >= 1000 ? "+∞" : `${absPct.toFixed(absPct < 10 ? 1 : 0)}%`;
  const tooltip = `${prevLabel}: ${fmt(prv)}`;
  return (
    <span
      className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-semibold tracking-wide ${tone}`}
      title={tooltip}
      data-testid="trend-chip"
    >
      <Icon size={9} weight="bold" />
      {label}
    </span>
  );
}

function StoreTable({ stores, totals, showTotal }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="store-table">
        <thead className="bg-[#F8FAFC]">
          <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF]">
            <th className="px-4 py-3 font-medium">Store</th>
            <th className="px-4 py-3 font-medium text-right">Sales</th>
            <th className="px-4 py-3 font-medium text-right">Revenue</th>
            <th className="px-4 py-3 font-medium text-right">Avg order</th>
            <th className="px-4 py-3 font-medium text-right">Items</th>
            <th className="px-4 py-3 font-medium text-right">Paid</th>
            <th className="px-4 py-3 font-medium text-right">Deposits</th>
            <th className="px-4 py-3 font-medium text-right">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          {stores.map((s) => (
            <tr key={s.store_id} className="border-t border-[#E5E7EB] hover:bg-[#F8FAFC]" data-testid={`store-row-${s.store_id}`}>
              <td className="px-4 py-3 font-semibold text-[#121216]">{s.store_name}</td>
              <td className="px-4 py-3 text-right font-mono">{num(s.sale_count)}</td>
              <td className="px-4 py-3 text-right font-mono font-bold text-[#121216]">
                {money(s.revenue_total ?? s.revenue)}
                {s.history?.revenue > 0.005 && (
                  <div className="text-[10px] font-normal text-[#B45309] mt-0.5" data-testid={`history-hint-${s.store_id}`}>
                    incl. {money(s.history.revenue)} history
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-right font-mono">{money(s.avg_order)}</td>
              <td className="px-4 py-3 text-right font-mono">{num(s.item_count)}</td>
              <td className="px-4 py-3 text-right font-mono text-[#166534]">{money(s.paid_revenue)}</td>
              <td className="px-4 py-3 text-right font-mono text-[#1E40AF]">{money(s.deposit_revenue)}</td>
              <td className={`px-4 py-3 text-right font-mono ${s.outstanding_balance > 0.005 ? "text-[#B91C1C] font-bold" : "text-[#9CA3AF]"}`}>
                {money(s.outstanding_balance)}
              </td>
            </tr>
          ))}
          {showTotal && (
            <tr className="border-t-2 border-[#121216] bg-[#F8FAFC]" data-testid="store-row-total">
              <td className="px-4 py-3 font-bold uppercase tracking-wider text-[10px]">Total</td>
              <td className="px-4 py-3 text-right font-mono font-bold">{num(totals.sale_count)}</td>
              <td className="px-4 py-3 text-right font-mono font-bold text-[#121216]">
                {money(totals.revenue_total ?? totals.revenue)}
                {totals.history_revenue > 0.005 && (
                  <div className="text-[10px] font-normal text-[#B45309] mt-0.5">
                    incl. {money(totals.history_revenue)} history
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-right font-mono font-bold">{money(totals.avg_order)}</td>
              <td className="px-4 py-3 text-right font-mono font-bold">{num(totals.item_count)}</td>
              <td className="px-4 py-3 text-right font-mono font-bold text-[#166534]">{money(totals.paid_revenue)}</td>
              <td className="px-4 py-3 text-right font-mono font-bold text-[#1E40AF]">{money(totals.deposit_revenue)}</td>
              <td className={`px-4 py-3 text-right font-mono font-bold ${totals.outstanding_balance > 0.005 ? "text-[#B91C1C]" : "text-[#9CA3AF]"}`}>
                {money(totals.outstanding_balance)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PaymentCard({ store }) {
  const methods = Object.entries(store.payment_methods || {});
  const total = methods.reduce((s, [, v]) => s + Number(v || 0), 0);
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid={`payment-card-${store.store_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-[14px] text-[#121216] truncate">{store.store_name}</div>
        <div className="text-[11px] text-[#9CA3AF] uppercase tracking-[0.14em]">Payments</div>
      </div>
      {methods.length === 0 ? (
        <div className="text-[12px] text-[#9CA3AF]">No payments in this period.</div>
      ) : (
        <div className="space-y-2">
          {methods.map(([m, v]) => {
            const pct = total > 0 ? (Number(v) / total) * 100 : 0;
            // Distinct colour for Tyro EFTPOS so the cert assessor can
            // see the bucket at a glance vs. card-manual / cash.
            const barColour = m === "tyro" ? "#1E40AF"
                            : m === "cash" ? "#0F9D58"
                            : "#1E5FEB";
            const label = m === "tyro" ? "TYRO EFTPOS" : m.replace("_", " ").toUpperCase();
            return (
              <div key={m}>
                <div className="flex items-center justify-between text-[12px] mb-1">
                  <span className="font-semibold text-[#121216]">{label}</span>
                  <span className="font-mono text-[#4B5563]">{money(v)} · {pct.toFixed(0)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, background: barColour }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tyro Integrated Reports — cert HR'd item. Only renders when the
          current store has a paired terminal, matching Reports page
          "single-store" scope (non-admins already pinned to one store). */}
      <TyroReconciliationCard />
    </div>
  );
}
